import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { selectors as filterBarSelectors } from '../../filterBar';
import {
  UsageAnalyticsActivity,
  UsageData,
} from '../types';
import { OWNER_TYPES } from '../constants';
import { usageAnalyticsSelector } from '../selectors';

const { ORG, TEAM } = OWNER_TYPES;

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useTransformUsageData = () => {
  const ownerType = useSelector(filterBarSelectors.selectOwnerType);
  const { activity } = useSelector(usageAnalyticsSelector);

  const { usageData, teamIds } = useMemo(() => {
    // Each row has all the date / time data, so generate the x-axis using the dates provided in
    // the response
    const xAxis = activity?.map(({ time }) => time);
    // Generate an array of objects we'll use to fill both the chart data & map into table rows
    // for display / browsing.
    const data: UsageData[] = xAxis?.map((time) => ({
      key: time.getTime(),
      time,
    })) ?? [];

    // Team IDs array is used to generate all unique columns on the chart. Each team has a key in
    // the chart data, and the value as the percent visible on the chart.
    let ids: string[] = [];

    const activityItem = activity.find((item) => item);
    // we don't have a limit on ticks with no usage
    // fall back to the first limit we found or leave it undefined
    // mind that this could lead to wrong data displayed but is the best we can do right now
    const firstActivity = activity.find((item) => item.values.length !== 0);
    const fallbackLimit = firstActivity?.values[0].limits.resource_original;

    if (activityItem) {
      if (ownerType === TEAM && activityItem.teamId) {
        const { teamId } = activityItem as UsageAnalyticsActivity<typeof TEAM>;
        ids.push(teamId);

        // If we're in team mode, only a single team can be in the response, so map the data from
        // that single team into our chart data using the min / max / avg from just that team.
        activity.forEach((value, index) => {
          data[index] = {
            ...data[index],
            [teamId]: {
              concurrencyAverage: value.values[0]?.concurrency.max ?? 0,
            },
            limit: value.values[0]?.limits.resource_original ?? fallbackLimit,
            peak: value.values[0]?.concurrency.max ?? 0,
          };
        });
      } else if (ownerType === ORG) {
        // If we're in org mode, this can contain many objects from various teams. Loop through
        // each value entry and generate table rows and chart data using the min / max / avg from
        // each
        activity.forEach((value, index) => {
          const teamShare = value.values[0]?.concurrency.max_org_concurrency_team_share ?? [];
          const peak = value.values[0]?.concurrency.max ?? 0;
          const limit = value.values[0]?.limits.resource ?? fallbackLimit;

          // creates a key->value object with team_id->percent_max_ccy/average_concurrency
          const teamEntries = Object.fromEntries(
            teamShare.map((team) => [
              team.team_id, {
                concurrencyAverage: team.avg_concurrency,
                usagePercentage: team.pct,
              },
            ]),
          );

          ids.push(...Object.keys(teamEntries));

          data[index] = {
            ...data[index],
            // Team entries are spread into the data (chart data) object.
            ...teamEntries,
            limit,
            peak,
          };
        });
      }

      // dedup team ids
      ids = ids.filter((value, index, array) => (
        array.indexOf(value) === index
      ));
    }

    return {
      // return data only if there was an activity in the given timeperiod
      usageData: firstActivity ? data : [],
      teamIds: ids,
    };
  }, [activity, ownerType]);

  return { usageData, teamIds };
};

export {
  useTransformUsageData,
};
